import { useRef, useState } from "react";
import { Plus, Trash2, Save, FileSpreadsheet, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const KINDS = ["standings", "stats", "schedule", "teams", "champions", "general"];

export function TableEditor({ table, onSaved, onDeleted }) {
  const [title, setTitle] = useState(table.title);
  const [kind, setKind] = useState(table.kind);
  const [note, setNote] = useState(table.note || "");
  const [columns, setColumns] = useState(table.columns?.length ? table.columns : ["Column 1"]);
  const [rows, setRows] = useState(table.rows?.length ? table.rows : [[""]]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const normalizeRows = (cols, rws) =>
    rws.map((r) => {
      const copy = [...r];
      while (copy.length < cols.length) copy.push("");
      return copy.slice(0, cols.length);
    });

  const setCell = (ri, ci, v) => {
    setRows((prev) => prev.map((r, i) => (i === ri ? r.map((c, j) => (j === ci ? v : c)) : r)));
  };

  const setColumnName = (ci, v) => setColumns((prev) => prev.map((c, i) => (i === ci ? v : c)));

  const addColumn = () => {
    const cols = [...columns, `Column ${columns.length + 1}`];
    setColumns(cols);
    setRows(normalizeRows(cols, rows));
  };

  const removeColumn = (ci) => {
    if (columns.length === 1) return;
    const cols = columns.filter((_, i) => i !== ci);
    setColumns(cols);
    setRows(rows.map((r) => r.filter((_, i) => i !== ci)));
  };

  const addRow = () => setRows([...rows, columns.map(() => "")]);
  const removeRow = (ri) => setRows(rows.filter((_, i) => i !== ri));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/parse", form, { headers: { "Content-Type": "multipart/form-data" } });
      setColumns(data.columns.length ? data.columns : ["Column 1"]);
      setRows(data.rows.length ? data.rows : [data.columns.map(() => "")]);
      toast.success(`Loaded ${data.rows.length} rows from spreadsheet`);
    } catch (err) {
      toast.error("Could not read that file. Use .csv or .xlsx");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { title, kind, note, columns, rows: normalizeRows(columns, rows), order: table.order || 0 };
      await api.put(`/admin/tables/${table.id}`, payload);
      toast.success("Table saved");
      onSaved?.();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete table "${title}"?`)) return;
    await api.delete(`/admin/tables/${table.id}`);
    toast.success("Table deleted");
    onDeleted?.();
  };

  return (
    <div className="border border-[#E5E7EB] rounded-lg p-4 bg-white" data-testid={`table-editor-${table.id}`}>
      {table.source === "fargorate" && (
        <div className="mb-3 text-xs bg-blue-50 border border-blue-200 text-[#174e9c] rounded px-3 py-2">
          Auto-synced from FargoRate — manual edits here will be replaced on the next sync.
        </div>
      )}
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-[#555555] mb-1">Table title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid={`table-title-input-${table.id}`}
            className="w-full rounded-md border border-[#E5E7EB] px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#555555] mb-1">Type</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            data-testid={`table-kind-select-${table.id}`}
            className="rounded-md border border-[#E5E7EB] px-3 py-2 text-sm bg-white"
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`table-upload-${table.id}`}
          className="inline-flex items-center gap-2 text-sm border border-[#62A8E5] text-[#1E63C4] rounded-md px-3 py-2 hover:bg-blue-50 transition-colors"
        >
          <FileSpreadsheet className="h-4 w-4" /> Import CSV/Excel
        </button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleUpload} className="hidden" />
      </div>

      <div className="mb-3">
        <label className="block text-xs font-medium text-[#555555] mb-1">Note (optional)</label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Updated after Week 7"
          className="w-full rounded-md border border-[#E5E7EB] px-3 py-2 text-sm"
        />
      </div>

      <div className="overflow-x-auto border border-[#E5E7EB] rounded-md">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {columns.map((c, ci) => (
                <th key={ci} className="p-1 border-b border-r border-[#E5E7EB]">
                  <div className="flex items-center gap-1">
                    <input
                      value={c}
                      onChange={(e) => setColumnName(ci, e.target.value)}
                      className="w-full min-w-[90px] rounded px-2 py-1 text-xs font-semibold bg-white border border-gray-200"
                    />
                    <button type="button" onClick={() => removeColumn(ci)} className="text-red-400 hover:text-red-600">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </th>
              ))}
              <th className="p-1 border-b border-[#E5E7EB]">
                <button type="button" onClick={addColumn} className="text-[#1E63C4]" title="Add column" data-testid={`add-column-${table.id}`}>
                  <Plus className="h-4 w-4" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {columns.map((_, ci) => (
                  <td key={ci} className="border-b border-r border-[#E5E7EB] p-0">
                    <input
                      value={row[ci] ?? ""}
                      onChange={(e) => setCell(ri, ci, e.target.value)}
                      className="w-full min-w-[90px] px-2 py-1.5 focus:bg-blue-50 focus:outline-none"
                    />
                  </td>
                ))}
                <td className="border-b border-[#E5E7EB] text-center">
                  <button type="button" onClick={() => removeRow(ri)} className="text-red-400 hover:text-red-600 p-1">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button
          type="button"
          onClick={addRow}
          data-testid={`add-row-${table.id}`}
          className="inline-flex items-center gap-1 text-sm border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-gray-50"
        >
          <Plus className="h-4 w-4" /> Add row
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          data-testid={`save-table-${table.id}`}
          className="inline-flex items-center gap-1 text-sm bg-[#1E63C4] text-white rounded-md px-4 py-2 hover:bg-[#174e9c] disabled:opacity-60"
        >
          <Save className="h-4 w-4" /> Save table
        </button>
        <button
          type="button"
          onClick={del}
          data-testid={`delete-table-${table.id}`}
          className="inline-flex items-center gap-1 text-sm text-red-600 rounded-md px-3 py-2 hover:bg-red-50 ml-auto"
        >
          <Trash2 className="h-4 w-4" /> Delete
        </button>
      </div>
    </div>
  );
}
